import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import * as HeroIconsOutline from '@heroicons/react/24/outline';

const HERO_ICONS = [
    { name: 'UserIcon', label: 'User' },
    { name: 'GlobeAltIcon', label: 'Globe' },
    { name: 'BriefcaseIcon', label: 'Briefcase' },
    { name: 'CurrencyDollarIcon', label: 'Dollar' },
    { name: 'DocumentTextIcon', label: 'Document' },
    { name: 'ShieldCheckIcon', label: 'Shield' },
    { name: 'StarIcon', label: 'Star' },
    { name: 'Square2StackIcon', label: 'Square Stack' },
    { name: 'ClockIcon', label: 'Clock' },
    { name: 'IdentificationIcon', label: 'ID Card' },
    { name: 'HomeIcon', label: 'Home' },
    { name: 'BuildingLibraryIcon', label: 'Building' },
    { name: 'ChartBarIcon', label: 'Chart' },
];

const GRADIENT_THEMES = [
    { from: 'blue-500', to: 'indigo-600', label: 'Classic Blue' },
    { from: 'green-500', to: 'lime-400', label: 'Fresh Green' },
    { from: 'pink-500', to: 'rose-400', label: 'Soft Pink' },
    { from: 'orange-500', to: 'amber-400', label: 'Warm Orange' },
    { from: 'purple-500', to: 'indigo-400', label: 'Royal Purple' },
    { from: 'teal-500', to: 'emerald-400', label: 'Teal Emerald' },
    { from: 'yellow-500', to: 'orange-400', label: 'Golden Yellow' },
    { from: 'red-500', to: 'orange-400', label: 'Vibrant Red' },
];

const EligibilityModal = ({ isOpen, onClose, onSubmit, criteria, loading, loanType }) => {
    const [formData, setFormData] = useState({
        criteria: '',
        icon_name: 'UserIcon',
        gradient_from: 'blue-500',
        gradient_to: 'indigo-600',
        display_order: 0,
        is_active: true,
        loan_type: loanType
    });

    useEffect(() => {
        if (criteria) {
            setFormData({
                ...criteria,
                is_active: criteria.is_active === 1 || criteria.is_active === true
            });
        } else {
            setFormData({
                criteria: '',
                icon_name: 'UserIcon',
                gradient_from: 'blue-500',
                gradient_to: 'indigo-600',
                display_order: 0,
                is_active: true,
                loan_type: loanType
            });
        }
    }, [criteria, loanType]);

    if (!isOpen) return null;

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit(formData);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
                <div className="flex items-center justify-between px-8 py-6 border-b border-slate-100">
                    <h2 className="text-2xl font-bold text-slate-800">
                        {criteria ? 'Edit Eligibility Criteria' : 'Add New Eligibility Criteria'}
                    </h2>
                    <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full transition-colors">
                        <XMarkIcon className="h-6 w-6 text-slate-500" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-8 space-y-6 max-h-[70vh] overflow-y-auto">
                    {/* Criteria Text */}
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Criteria Description
                        </label>
                        <textarea
                            required
                            rows="3"
                            value={formData.criteria}
                            onChange={(e) => setFormData({ ...formData, criteria: e.target.value })}
                            className="w-full px-4 py-3 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none"
                            placeholder="Enter eligibility criteria description..."
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Display Order */}
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Display Order
                            </label>
                            <input
                                type="number"
                                value={formData.display_order}
                                onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
                                className="w-full px-4 py-3 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                            />
                        </div>

                        {/* Active Status */}
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Status
                            </label>
                            <button
                                type="button"
                                onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                                className={`w-full flex items-center justify-between px-6 py-3 rounded-2xl border transition-all ${formData.is_active
                                        ? 'bg-green-50 border-green-200 text-green-700'
                                        : 'bg-slate-50 border-slate-200 text-slate-500'
                                    }`}
                            >
                                <span className="font-semibold">{formData.is_active ? 'Active' : 'Inactive'}</span>
                                <div className={`w-10 h-6 rounded-full relative transition-all ${formData.is_active ? 'bg-green-500' : 'bg-slate-300'}`}>
                                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${formData.is_active ? 'left-5' : 'left-1'}`}></div>
                                </div>
                            </button>
                        </div>
                    </div>

                    {/* Icon Selection */}
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-4">
                            Select Icon
                        </label>
                        <div className="grid grid-cols-4 sm:grid-cols-7 gap-3">
                            {HERO_ICONS.map((icon) => {
                                const IconComp = HeroIconsOutline[icon.name];
                                return (
                                    <button
                                        key={icon.name}
                                        type="button"
                                        onClick={() => setFormData({ ...formData, icon_name: icon.name })}
                                        className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-1 ${formData.icon_name === icon.name
                                                ? 'border-primary-500 bg-primary-50 text-primary-600'
                                                : 'border-slate-100 bg-white text-slate-400 hover:border-slate-200'
                                            }`}
                                    >
                                        <IconComp className="h-6 w-6" />
                                        <span className="text-[10px] whitespace-nowrap overflow-hidden text-ellipsis w-full">{icon.label}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Gradient Theme Selection */}
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-4">
                            Select Color Theme
                        </label>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            {GRADIENT_THEMES.map((theme) => (
                                <button
                                    key={theme.label}
                                    type="button"
                                    onClick={() => setFormData({ ...formData, gradient_from: theme.from, gradient_to: theme.to })}
                                    className={`p-3 rounded-xl border-2 transition-all text-left ${formData.gradient_from === theme.from
                                            ? 'border-primary-500 bg-white ring-2 ring-primary-100'
                                            : 'border-slate-100 bg-white hover:border-slate-200'
                                        }`}
                                >
                                    <div className={`h-8 w-full rounded-lg bg-gradient-to-r from-${theme.from} to-${theme.to} mb-2`}></div>
                                    <span className="text-xs font-semibold text-slate-600">{theme.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </form>

                <div className="px-8 py-6 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-6 py-3 rounded-xl font-semibold text-slate-600 hover:bg-slate-200 transition-all"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={loading}
                        className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-xl font-semibold transition-all disabled:opacity-50 shadow-lg"
                    >
                        {loading ? 'Saving...' : criteria ? 'Update Criteria' : 'Create Criteria'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EligibilityModal;
